import { VStack, Text, Spinner, Center } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { useLedgerHistory } from '@/lib/hooks/useLedgerHistory';
import { LedgerHistoryCard } from './LedgerHistoryCard';
import type { HexString } from '@/lib/config/wagmi';

interface LedgerHistorySectionProps {
  userAddress: HexString;
  onViewAll?: () => void;
  hideHeader?: boolean;
}

export function LedgerHistorySection({
  userAddress,
  onViewAll,
  hideHeader = false,
}: LedgerHistorySectionProps) {
  const t = useTranslations('trader.ledgerHistory');
  const {
    data: ledgerHistory,
    isLoading,
    error,
  } = useLedgerHistory(userAddress);

  if (isLoading) {
    return (
      <Center py={8}>
        <Spinner size="lg" color="blue.500" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center py={8}>
        <Text color="red.500" fontSize="sm">
          {t('error')}
        </Text>
      </Center>
    );
  }

  if (!ledgerHistory || ledgerHistory.length === 0) {
    return (
      <VStack gap={4} py={8}>
        <Text fontSize="sm" color="white/60" textAlign="center">
          {t('noHistory')}
        </Text>
      </VStack>
    );
  }

  return (
    <VStack gap={4} align="stretch" w="full" px={4}>
      {!hideHeader && (
        <VStack gap={2} align="start">
          <Text fontSize="lg" fontWeight="semibold" color="white">
            {t('title')}
          </Text>
          <Text fontSize="sm" color="white/60">
            {t('count', { count: ledgerHistory.length })}
          </Text>
        </VStack>
      )}

      <VStack gap={3} align="stretch">
        {ledgerHistory.map((ledgerUpdate, index) => (
          <LedgerHistoryCard
            key={`${ledgerUpdate.hash}-${index}`}
            ledgerUpdate={ledgerUpdate}
          />
        ))}
      </VStack>

      {onViewAll && ledgerHistory.length > 0 && (
        <Text
          fontSize="sm"
          color="blue.400"
          textAlign="center"
          cursor="pointer"
          _hover={{ color: 'blue.300' }}
          onClick={onViewAll}
        >
          {t('viewAll')}
        </Text>
      )}
    </VStack>
  );
}
